import React, { useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import Transition from "../../../../../components/Transition";
import Toast from "../../../../../components/Toast";
import ButtonLoader from "../../../../../components/ButtonLoader";
import { BrokersData } from "../network/api/BrokersData";

const normalizeOptionalValue = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value);
};

const normalizeServerRows = (broker) => {
    if (Array.isArray(broker?.servers) && broker.servers.length > 0) {
        return broker.servers.map((server) => ({
            id: server?.id ?? null,
            server: normalizeOptionalValue(server?.server),
            terminal_url: normalizeOptionalValue(server?.terminal_url),
        }));
    }

    if (broker?.server || broker?.terminal_url) {
        return [{
            id: broker?.server_id ?? null,
            server: normalizeOptionalValue(broker?.server),
            terminal_url: normalizeOptionalValue(broker?.terminal_url),
        }];
    }

    return [{
        id: null,
        server: "",
        terminal_url: "",
    }];
};

const createEmptyServerRow = () => ({
    id: null,
    server: "",
    terminal_url: "",
});

export default function BrokerForm({
    setEditMode,
    selectedBroker = null,
    canManageBrokers = false,
    onSaved = () => {},
}) {
    const [name, setName] = useState(selectedBroker?.name || "");
    const [brokerIb, setBrokerIb] = useState(normalizeOptionalValue(selectedBroker?.broker_ib));
    const [imageUrl, setImageUrl] = useState(normalizeOptionalValue(selectedBroker?.image_url));
    const [description, setDescription] = useState(normalizeOptionalValue(selectedBroker?.description));
    const [servers, setServers] = useState(normalizeServerRows(selectedBroker));
    const [isLoading, setIsLoading] = useState(false);
    const [toastConfig, setToastConfig] = useState({
        message: "",
        type: "primary",
        visible: false,
    });

    useEffect(() => {
        setName(selectedBroker?.name || "");
        setBrokerIb(normalizeOptionalValue(selectedBroker?.broker_ib));
        setImageUrl(normalizeOptionalValue(selectedBroker?.image_url));
        setDescription(normalizeOptionalValue(selectedBroker?.description));
        setServers(normalizeServerRows(selectedBroker));
    }, [selectedBroker]);

    const updateServerRow = (index, field, value) => {
        setServers((prev) => prev.map((row, rowIndex) => (
            rowIndex === index ? { ...row, [field]: value } : row
        )));
    };

    const addServerRow = () => {
        setServers((prev) => [...prev, createEmptyServerRow()]);
    };

    const removeServerRow = (index) => {
        setServers((prev) => {
            if (prev.length <= 1) {
                return prev;
            }

            return prev.filter((_, rowIndex) => rowIndex !== index);
        });
    };

    const handleSubmit = async () => {
        if (!canManageBrokers) {
            setToastConfig({
                message: "Trusted super admin access is required to manage brokers.",
                type: "danger",
                visible: true,
            });
            return;
        }

        if (!name.trim()) {
            setToastConfig({
                message: "Broker name is required.",
                type: "danger",
                visible: true,
            });
            return;
        }

        const cleanedServers = servers
            .map((row) => ({
                server: normalizeOptionalValue(row.server).trim(),
                terminal_url: normalizeOptionalValue(row.terminal_url).trim(),
            }))
            .filter((row) => row.server && row.terminal_url);

        if (cleanedServers.length === 0) {
            setToastConfig({
                message: "Add at least one server with a server name and terminal URL.",
                type: "danger",
                visible: true,
            });
            return;
        }

        setIsLoading(true);

        const payload = {
            id: selectedBroker?.id,
            name: name.trim(),
            broker_ib: brokerIb.trim() || null,
            image_url: imageUrl.trim() || null,
            description: description.trim() || null,
            servers: cleanedServers,
        };

        const response = await BrokersData({
            method: selectedBroker?.id ? "PATCH" : "POST",
            formData: payload,
        });

        setIsLoading(false);

        if (response.status === "success") {
            setToastConfig({
                message: selectedBroker?.id ? "Broker updated successfully." : "Broker created successfully.",
                type: "success",
                visible: true,
            });

            onSaved();
            setTimeout(() => {
                setEditMode(false);
            }, 800);
        } else {
            setToastConfig({
                message: response.message || "Unable to save broker.",
                type: "danger",
                visible: true,
            });
        }
    };

    return (
        <AnimatePresence mode="wait">
            <Transition key="broker-form-page">
                <Toast
                    message={toastConfig.message}
                    type={toastConfig.type}
                    visible={toastConfig.visible}
                    setVisible={(visible) => setToastConfig({ ...toastConfig, visible })}
                />

                <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h4 className="card-title">{selectedBroker?.id ? "Edit" : "Create"} Broker</h4>
                        <button className="btn btn-primary btn-sm" onClick={() => setEditMode(false)}>
                            <i className="fa fa-arrow-left"></i> Back
                        </button>
                    </div>

                    <div className="card-body">
                        {!canManageBrokers && (
                            <div className="alert alert-warning alert-alt solid">
                                Trusted super admin access is required for create and update actions.
                            </div>
                        )}

                        <div className="row">
                            <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label">Broker Name</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    placeholder="Broker name"
                                    disabled={!canManageBrokers || isLoading}
                                />
                            </div>

                            <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label">IB URL</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    value={brokerIb}
                                    onChange={(event) => setBrokerIb(event.target.value)}
                                    placeholder="https://example.com/ib-link"
                                    disabled={!canManageBrokers || isLoading}
                                />
                            </div>

                            <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label">Image URL</label>
                                <input
                                    type="url"
                                    className="form-control"
                                    value={imageUrl}
                                    onChange={(event) => setImageUrl(event.target.value)}
                                    placeholder="https://example.com/logo.png"
                                    disabled={!canManageBrokers || isLoading}
                                />
                            </div>

                            <div className="col-lg-6 col-12 mb-3">
                                <label className="form-label">Description</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={description}
                                    onChange={(event) => setDescription(event.target.value)}
                                    placeholder="Optional description"
                                    disabled={!canManageBrokers || isLoading}
                                />
                            </div>

                            {imageUrl ? (
                                <div className="col-12 mb-3">
                                    <label className="form-label d-block">Image Preview</label>
                                    <img
                                        src={imageUrl}
                                        alt="Broker preview"
                                        className="img-fluid rounded border"
                                        style={{ maxHeight: "160px", objectFit: "contain" }}
                                    />
                                </div>
                            ) : null}

                            <div className="col-12">
                                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                    <div>
                                        <h5 className="mb-1">Servers</h5>
                                        <p className="text-muted mb-0 small">
                                            Add every server that belongs to this broker.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={addServerRow}
                                        disabled={!canManageBrokers || isLoading}
                                    >
                                        <i className="fa fa-plus me-2"></i>Add Server
                                    </button>
                                </div>

                                {servers.map((serverRow, index) => (
                                    <div key={serverRow.id || index} className="border rounded p-3 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <strong>Server {index + 1}</strong>
                                            <button
                                                type="button"
                                                className="btn btn-outline-danger btn-sm"
                                                onClick={() => removeServerRow(index)}
                                                disabled={!canManageBrokers || isLoading || servers.length <= 1}
                                            >
                                                Remove
                                            </button>
                                        </div>

                                        <div className="row">
                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Server Name</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={serverRow.server}
                                                    onChange={(event) => updateServerRow(index, "server", event.target.value)}
                                                    placeholder="Server name"
                                                    disabled={!canManageBrokers || isLoading}
                                                />
                                            </div>

                                            <div className="col-lg-6 col-12 mb-3">
                                                <label className="form-label">Terminal URL</label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    value={serverRow.terminal_url}
                                                    onChange={(event) => updateServerRow(index, "terminal_url", event.target.value)}
                                                    placeholder="https://example.com/terminal"
                                                    disabled={!canManageBrokers || isLoading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button
                            className="btn btn-primary mt-2"
                            onClick={handleSubmit}
                            disabled={isLoading || !canManageBrokers}
                        >
                            {isLoading ? <ButtonLoader /> : <><i className="fa fa-save me-2"></i>Save Broker</>}
                        </button>
                    </div>
                </div>
            </Transition>
        </AnimatePresence>
    );
}
